import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Message, Table } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { store } from '../../store';
import additionalMessages from '../../messages';
import { postFileWithAuth } from '../../api';
import { uiSet, loadProject } from '../mainframe/mainframe.reducer';
import { clearSocketMessages } from '../../reducers/socket_reducer';
import { useSelector } from 'react-redux';

const DEFAULT_IMPORT_STATE = {
  busy: false,
  importFile: null,
  message: null,
  // Set when the import succeeded but some configurations were changed
  // because they are no longer supported.
  review: null, // { projectId, warnings }
};

const prettify = (attr) => attr.replace(/_/g, ' ');

// Shown after a successful import when some settings were not supported and
// were replaced with defaults. One row per link/end: the setting that could
// not be applied, the value in the file and the value now in use.
const ImportWarnings = ({ warnings }) => {
  const rows = warnings.filter((w) => w.attribute);
  const notes = warnings.filter((w) => !w.attribute);
  return (
    <>
      <Message info>
        <Message.Header>Your project has been imported</Message.Header>
        <p>
          {rows.length > 0 &&
            'Some settings are not supported by this version, so default values are being used instead. '}
          {notes.length > 0 &&
            'Some features in the original project were not imported.'}
        </p>
      </Message>
      {rows.length > 0 && (
        <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
          <Table compact size="small" basic="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Link / end</Table.HeaderCell>
                <Table.HeaderCell>Setting</Table.HeaderCell>
                <Table.HeaderCell>In your file</Table.HeaderCell>
                <Table.HeaderCell>Now using</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((w, i) => (
                <Table.Row key={i} verticalAlign="top">
                  <Table.Cell>{w.object}</Table.Cell>
                  <Table.Cell>
                    {prettify(w.attribute)}
                    {w.also_changed.length > 0 && (
                      <div style={{ color: 'grey', fontSize: '0.9em' }}>
                        and {w.also_changed.length} related setting
                        {w.also_changed.length === 1 ? '' : 's'}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>{w.intended || '-'}</Table.Cell>
                  <Table.Cell>{w.actual || '-'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
      {notes.length > 0 && (
        <>
          <strong>Not imported</strong>
          <ul>
            {notes.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
};

const ImportProjectDialog = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const importRef = React.createRef();

  const [importState, setImportState] = useState(DEFAULT_IMPORT_STATE);
  const { busy } = importState;
  const show = useSelector((state) => state.mainFrame.showLPImportDialog);
  const [projectId, status = '', action, message, warnings] = useSelector(
    (state) => [
      state.sockets.projectId,
      state.sockets.status,
      state.sockets.action,
      state.sockets.message,
      state.sockets.warnings,
    ]
  );

  const openProject = (id) => {
    console.log('Import successful, loading project ', id);
    store.dispatch(clearSocketMessages());
    store.dispatch(uiSet({ showLPImportDialog: false }));
    store.dispatch(loadProject({ projectId: id }));
    setImportState(DEFAULT_IMPORT_STATE);
    navigate('/');
  };

  // Handle the socket messages that appear when the worker
  // imports the project
  useEffect(() => {
    if (status === 'SUCCESS' && action === 'LINKPLANNER_IMPORT') {
      if (warnings?.length) {
        // Let the user see which configurations changed before opening.
        store.dispatch(clearSocketMessages());
        setImportState({
          ...DEFAULT_IMPORT_STATE,
          review: { projectId, warnings },
        });
      } else {
        openProject(projectId);
      }
    } else if (status === 'BUSY') {
      setImportState({
        ...importState,
        busy: true,
        message: message,
      });
    } else if (status === 'FAILED' && action === 'LINKPLANNER_IMPORT') {
      const displayMessage =
        message ?? formatMessage(additionalMessages.fileError);
      store.dispatch(clearSocketMessages());
      setImportState({
        ...importState,
        busy: false,
        message: displayMessage,
      });
    }
  }, [projectId, status, action, message, warnings]);

  const doImport = () => {
    if (importState.importFile) {
      setImportState({ ...importState, busy: true });
      postFileWithAuth('projects/linkplanner', importState.importFile)
        .then((res) => {
          // TODO: Check for error messages
          // Leave busy running until the task is complete
        })
        .catch((e) => {
          if (e === 'Invalid file')
            setImportState({
              ...importState,
              busy: false,
              message: formatMessage(additionalMessages.invalidFileError),
            });
          else
            setImportState({
              ...importState,
              busy: false,
              message: formatMessage(additionalMessages.fileError),
            });
        });
    }
  };

  const onClose = () => {
    setImportState(DEFAULT_IMPORT_STATE);
    store.dispatch(clearSocketMessages());
    store.dispatch(uiSet({ showLPImportDialog: false }));
  };

  const onOpen = () => {
    setImportState(DEFAULT_IMPORT_STATE);
    store.dispatch(clearSocketMessages());
  };

  const onImportFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const body = new FormData();
    body.append('file', file);
    body.append('filename', file.name);
    setImportState({ ...importState, importFile: body, message: null });
  };

  const getWarningMessage = (formatMessage) => {
    const msg = importState.message;
    if (msg != null) {
      return msg;
    } else {
      return formatMessage(additionalMessages.fileError);
    }
  };

  const { review } = importState;

  // The project has already been created, so the only way forward is to
  // acknowledge the changes and open it.
  const reviewActions = review && (
    <Button color="blue" onClick={() => openProject(review.projectId)}>
      {formatMessage(additionalMessages.ok)}
    </Button>
  );

  const okButton = busy ? (
    <Button icon="sync" color="blue" loading />
  ) : (
    <Button
      onClick={() => doImport()}
      color="blue"
      disabled={!importState.importFile || busy || !!message}
    >
      {formatMessage(additionalMessages.ok)}
    </Button>
  );

  return (
    <Modal
      open={show}
      onClose={() => (review ? openProject(review.projectId) : onClose())}
      onOpen={() => onOpen()}
      closeOnDimmerClick={false}
    >
      <Modal.Header>
        {formatMessage(additionalMessages.importLINKPlannerProject)}
        {review ? null : (
          <Button
            circular
            icon="close"
            title="Close"
            floated="right"
            onClick={() => onClose()}
          />
        )}
      </Modal.Header>
      <Modal.Content scrolling={!!review}>
        {review ? (
          <ImportWarnings warnings={review.warnings} />
        ) : (
          <>
            <Button
              content={formatMessage(additionalMessages.selectLPFile)}
              labelPosition="left"
              icon={{ className: 'linkplanner-icon-coloured' }}
              onClick={() => importRef.current.click()}
              disabled={busy}
            />
            <input
              ref={importRef}
              type="file"
              accept=".ptpprj,.lpp"
              hidden
              onChange={onImportFileChange}
            />
            {importState.importFile && importState.importFile.get('filename')}

            {importState.message ? (
              <Message
                error={!busy}
                onDismiss={
                  busy
                    ? null
                    : () => {
                        setImportState({ ...importState, message: null });
                      }
                }
                header={
                  busy
                    ? formatMessage(additionalMessages.importProgress)
                    : formatMessage(additionalMessages.importError)
                }
                content={
                  busy ? importState.message : getWarningMessage(formatMessage)
                }
              />
            ) : null}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        {busy || review ? null : (
          <Button onClick={() => onClose()}>
            {formatMessage(additionalMessages.cancel)}
          </Button>
        )}
        {review ? reviewActions : okButton}
      </Modal.Actions>
    </Modal>
  );
};

export default ImportProjectDialog;
